import React from 'react'
import { teamNick } from './plain'

/**
 * NFL UI primitives — a self-contained copy of the MLB UnifiedApp design system
 * (tokens, tier badges, nav, stat tiles, cards) so the NFL app is visually
 * identical to the MLB app without importing from / modifying the MLB monolith.
 * Kept isolated per the "don't interfere with MLB" rule.
 */
export const T = {
  bg:"#080C12", card:"#111827", card2:"#1A2130", card3:"#0C1F18",
  border:"#1E2A3A", border2:"#2A3548",
  green:"#00D97E", greenDim:"#00D97E18", greenDk:"#064E3B", greenMid:"#065F46", greenLt:"#DCFCE7",
  mint:"#34D399",
  amber:"#F59E0B", amberDim:"#F59E0B18",
  red:"#EF4444", redDim:"#EF444418",
  blue:"#3B82F6", blueDim:"#3B82F618", blueLt:"#1E3A5F",
  orange:"#F97316", purple:"#8B5CF6",
  text:"#F1F5F9", muted:"#A6B2C4", muted2:"#CBD5E1", faint:"#475569",
  mono:"'SF Mono','Fira Code',monospace",
}

export const TIER = {
  "ELITE":     {bg:"#DBEAFE", fg:"#1E3A8A", bd:"#2563EB", icon:"◆",  label:"ELITE"},
  "BEST BET":  {bg:"#DBEAFE", fg:"#1E40AF", bd:"#3B82F6", icon:"🔥", label:"TOP RATED"},
  "STRONG":    {bg:"#DCFCE7", fg:"#005C3A", bd:"#00D97E", icon:"✅", label:"STRONG"},
  "PLAY":      {bg:"#D1FAE5", fg:"#065F46", bd:"#34D399", icon:"👀", label:"MODERATE"},
  "WATCH":     {bg:"#FEF3C7", fg:"#7C3B0A", bd:"#F59E0B", icon:"👁", label:"WATCH"},
  "PASS":      {bg:"#FEE2E2", fg:"#7F1D1D", bd:"#EF4444", icon:"✕",  label:"LOW"},
}

// ── book names + odds formatting (line-shopping) ────────────────────────────
export const BOOK_NAMES = {
  draftkings:"DraftKings", fanduel:"FanDuel", betmgm:"BetMGM", caesars:"Caesars",
  espnbet:"ESPN Bet", fanatics:"Fanatics", betrivers:"BetRivers", pointsbetus:"PointsBet",
  hardrockbet:"Hard Rock", ballybet:"Bally Bet", bovada:"Bovada", betonlineag:"BetOnline",
  lowvig:"LowVig", mybookieag:"MyBookie", betus:"BetUS", williamhill_us:"Caesars",
  wynnbet:"WynnBET", superbook:"SuperBook", unibet_us:"Unibet",
  kalshi:"Kalshi", polymarket:"Polymarket", novig:"Novig", prophetx:"ProphetX",
}
export const bookName = k => BOOK_NAMES[k] || (k ? k.replace(/_/g,' ').replace(/\b\w/g, c=>c.toUpperCase()) : 'Book')
export const fmtOdds = o => o==null ? '—' : (o>0 ? `+${o}` : `${o}`)
// One-tap line shopping — map a book key to where the user can go place it. These are site
// links (true bet-slip deeplinks need the odds feed's per-bet links, which we don't ingest);
// a book-level link still gets them to the right sportsbook in one tap. null = no known link.
export const BOOK_URLS = {
  draftkings:"https://sportsbook.draftkings.com/", fanduel:"https://sportsbook.fanduel.com/",
  betmgm:"https://sports.betmgm.com/", caesars:"https://sportsbook.caesars.com/",
  williamhill_us:"https://sportsbook.caesars.com/", espnbet:"https://espnbet.com/",
  fanatics:"https://sportsbook.fanatics.com/", betrivers:"https://betrivers.com/",
  pointsbetus:"https://fanatics.com/", hardrockbet:"https://app.hardrock.bet/",
  ballybet:"https://play.ballybet.com/", bovada:"https://www.bovada.lv/sports",
  betonlineag:"https://www.betonline.ag/sportsbook", lowvig:"https://www.lowvig.ag/",
  mybookieag:"https://www.mybookie.ag/sportsbook/", betus:"https://www.betus.com.pa/sportsbook/",
  superbook:"https://superbook.com/", unibet_us:"https://www.unibet.com/",
  kalshi:"https://kalshi.com/matchups/nfl", polymarket:"https://polymarket.com/",
  novig:"https://novig.us/", prophetx:"https://prophetx.co/",
}
// Affiliate overlay — set once from /nfl/book-links at app load. A tracked affiliate URL wins
// over the plain book-site link, turning every "open the book" tap into a revenue link.
let _affil = {}
export const setBookAffiliates = (m) => { _affil = m || {} }
export const bookUrl = k => (_affil && _affil[k]) || BOOK_URLS[k] || null
// $100 stake payout for an American price (profit, not incl. stake) — for value copy.
export const payout100 = o => o==null ? 0 : (o>0 ? o : Math.round(10000/(-o)))

// ── helpers (mirror UnifiedApp) ─────────────────────────────────────────────
export const pct = v => `${Math.round((v||0)*100)}%`
export const probColor = p => p>=0.65?T.green : p>=0.55?"#34D399" : p>=0.45?T.amber : T.red
// Anytime-TD scale. Most scorers sit at 20–45%, so the over/under scale above (red under 45%)
// painted a strong 39% scorer red. Market-relative: 50%+ elite, 35%+ strong, 20%+ live; below
// that is a longshot — gray, not "bad" red.
export const tdColor = p => p>=0.5?T.green : p>=0.35?T.mint : p>=0.2?T.amber : T.muted
export const isTdPlay = e => !!e && (e.prop_type==='anytime_td' || e.market==='Anytime TD' || !!e._isTD)
// Color a play's % on the right scale for its market.
export const playColor = (p, e) => isTdPlay(e) ? tdColor(p) : probColor(p)
// The ONE probability a play shows: the backend's hit_prob (book-line prob when priced, else the
// reference-line prob, with the per-market calibration applied). Falls back to the raw book prob,
// then prob_over, only for payloads that predate hit_prob. Use this anywhere a play's % is shown so
// a card, a list and a hero can never disagree.
export const shownProb = e => {
  if (!e) return 0
  if (e.hit_prob != null) return e.hit_prob
  const priced = e.book_line != null && e.book_prob_over != null && e.market !== 'Anytime TD' && e.prop_type !== 'anytime_td'
  return (priced ? e.book_prob_over : e.prob_over) || 0
}

// Global game filter (set by the top GameTicker): keep a player row only when its team is one of the
// filtered game's two teams. `filter` is null (= all games) or { game_id, teams:[away,home], label }.
// Team codes differ by feed (LA/LAR, WAS/WSH, JAX/JAC) — compare on one spelling so a game filter
// never drops one side's players.
const _canonTeam = t => ({ LAR: 'LA', WSH: 'WAS', JAC: 'JAX' })[(t || '').toUpperCase()] || (t || '').toUpperCase()
export const inGameFilter = (filter, team) => !filter || filter.teams.some(t => _canonTeam(t) === _canonTeam(team))

// Weather at a glance — a bettor scanning the slate shouldn't have to open a game to learn
// it's blowing 20 on Sunday. Dome → no concern; outdoor with a forecast → wind (+ rain/cold);
// a far-out game whose forecast will still move reads "· forecast" so we never fake precision;
// outdoor with nothing loaded yet reads "forecast soon".
export function WeatherChip({ weather, style }){
  if(!weather) return null
  const base = { display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:700,
    borderRadius:7, padding:'2px 7px', border:`1px solid ${T.border}`, background:T.card2, whiteSpace:'nowrap', ...style }
  if(weather.indoor) return <span style={{...base, color:T.blue}}>🏟 Dome</span>
  if(weather.wind_mph==null) return <span style={{...base, color:T.faint}}>🌦 Forecast soon</span>
  const wet = (weather.precip||0) >= 0.1
  const windy = weather.wind_mph >= 15
  const cold = weather.temp_f!=null && weather.temp_f < 32
  const rough = wet || windy || cold
  const icon = wet ? '🌧' : windy ? '💨' : '🌤'
  return (
    <span style={{...base, color: rough ? T.amber : T.muted2}}>
      {icon} {weather.wind_mph} mph{wet?' · rain':''}{cold?` · ${weather.temp_f}°`:''}
      {!weather.final && <span style={{color:T.faint, fontWeight:600}}> · forecast</span>}
    </span>
  )
}

// The bet badge: when weather already moved the model's number for THIS prop (wind/cold dock
// passing & receiving), say so plainly on the card — ties weather to the bet AND shows the
// model handled it. `adj` is the projection's weather multiplier (<1 = trimmed). Negligible → null.
export function WeatherBadge({ adj, style }){
  if(adj==null || adj>=0.985) return null
  const dock = Math.round((1-adj)*100)
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10.5, fontWeight:700,
      color:T.amber, background:'rgba(245,166,35,0.12)', border:`1px solid ${T.amber}55`,
      borderRadius:6, padding:'2px 7px', whiteSpace:'nowrap', ...style }}>
      💨 Weather: model trimmed this ~{dock}%
    </span>
  )
}

// Horizontal chip/pill row with a right-edge fade hint so a phone user can tell there's
// more to scroll to. The fade hides once you've scrolled to the end.
export function ScrollRow({ children, style }){
  const ref = React.useRef(null)
  const [more, setMore] = React.useState(false)
  const check = () => { const el = ref.current; if(!el) return
    setMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 6) }   // content remains to the right
  React.useEffect(() => { check()
    const el = ref.current; if(!el) return
    el.addEventListener('scroll', check, { passive:true })
    window.addEventListener('resize', check)
    return () => { el.removeEventListener('scroll', check); window.removeEventListener('resize', check) }
  }, [children])
  return (
    <div style={{ position:'relative' }}>
      <div ref={ref} style={{ display:'flex', gap:6, overflowX:'auto', scrollbarWidth:'none', ...style }}>{children}</div>
      {more && <div aria-hidden style={{ position:'absolute', top:0, bottom:0, right:0, width:30, pointerEvents:'none',
        background:`linear-gradient(to right, transparent, ${T.bg})` }} />}
    </div>
  )
}
export const confColor = c => c>=85?T.blue : c>=70?T.green : c>=55?"#34D399" : c>=42?T.amber : T.red
export const edgeColor = e => e==null?T.muted : e>=3?T.green : e>=0?"#34D399" : T.red
export const gradeColor = g => g>=74?T.blue : g>=58?T.green : g>=50?T.amber : g>=38?T.orange : T.red
export const gradeLetter = g => g>=90?"A+":g>=80?"A":g>=74?"A-":g>=68?"B+":g>=58?"B":g>=50?"C":g>=38?"D":"F"
// Top tiers require CONFIDENCE, not just a high probability — a low-confidence read (a
// rookie on a generic role prior) can't read TOP RATED/ELITE off a shiny % alone. When a
// numeric edge (pp vs the book) is given, a recommended tier ALSO has to beat the price
// (mirrors the backend _tier): a priced play with no edge is a lean (PLAY), not a pick;
// edge null/undefined = unpriced, so the confidence tier stands. Prefer the API's `tier`
// field when a play carries one — this is only the client-side fallback.
const TIER_EDGE_MIN = { ELITE: 4.0, "BEST BET": 4.0, STRONG: 2.5 }
// anytime_td must SHOW at least this to be a recommended tier (mirrors backend _ATD_RECO_FLOOR):
// live calibration showed 60-65% ATD hitting ~31%, so below the floor an ATD play is a lean.
const ATD_RECO_FLOOR = 0.65
export const tierFromProb = (p, conf, edgePp, prop) => {
  const raw = conf>=85 && p>=0.62 ? "ELITE" : conf>=68 && p>=0.66 ? "BEST BET" : conf>=58 && p>=0.60 ? "STRONG" : p>=0.52 ? "PLAY" : p>=0.45 ? "WATCH" : "PASS"
  const recommended = raw in TIER_EDGE_MIN
  if (prop === "anytime_td" && recommended && p < ATD_RECO_FLOOR) return "PLAY"
  if (edgePp == null || !recommended) return raw
  if (edgePp < TIER_EDGE_MIN.STRONG) return "PLAY"
  if (edgePp < TIER_EDGE_MIN.ELITE) return "STRONG"
  return raw
}

// ONE rating across the app: the tier. RatingBadge used to show a letter (A+…C) derived from the
// tier — a second scale users had to decode. It now shows the tier name itself, colour-coded:
// green = recommended (Elite / Top Rated / Strong), amber = a lean, grey = below that.
// RATING_LETTER is kept only so old imports don't break; nothing user-facing reads it.
export const RATING_LETTER = {"ELITE":"A+","BEST BET":"A","STRONG":"A-","PLAY":"B","WATCH":"C+","PASS":"C"}
const _RECO = new Set(["ELITE","BEST BET","STRONG"])
export const tierColor = tier => _RECO.has(tier) ? T.green : tier==="PLAY" ? T.amber : T.muted2
export const tierLabel = tier => (TIER[tier]||TIER.WATCH).label
export function RatingBadge({tier,size=34}){
  const c = tierColor(tier), label = tierLabel(tier)
  return <span title={`Rating: ${label}`} style={{flex:"none",minWidth:Math.round(size*2.1),height:Math.round(size*0.72),
    borderRadius:7,display:"grid",placeItems:"center",padding:"0 6px",fontWeight:800,whiteSpace:"nowrap",
    fontSize:Math.max(10,Math.round(size*0.29)),letterSpacing:"0.05em",color:c,background:`${c}1A`,border:`1px solid ${c}55`}}>{label}</span>
}

// ── verdict chip (shared by props "The Case" and Fantasy start/sit) ──────────
// One visual language for a verdict across the app: a filled chip for the top
// call, coloured outlines below it. The vocabulary differs per surface (bets say
// "Strong / Leans yes"; fantasy says "SMASH / START") but the chip reads the same.
export const VLEVEL = {
  strong:  {c:"#04140C", b:T.green,       bd:T.green},        // filled — the top call
  good:    {c:T.green,   b:"transparent", bd:T.greenMid},
  neutral: {c:T.amber,   b:"transparent", bd:`${T.amber}66`},
  boom:    {c:T.purple,  b:"transparent", bd:`${T.purple}66`},
  warn:    {c:T.red,     b:"transparent", bd:`${T.red}66`},
  dead:    {c:T.muted,   b:"transparent", bd:T.border},
}
// Per-surface verdict → level maps (keep the words, share the look).
export const BET_VLEVEL  = {"Strong":"strong","Leans yes":"good","Mixed":"neutral","Tough":"warn","Priced out":"dead"}
export const FTSY_VLEVEL = {"SMASH":"strong","START":"good","FLEX":"neutral","BOOM / BUST":"boom","RISKY":"warn","SIT":"dead"}
export const vlevelColor = level => (VLEVEL[level]||VLEVEL.neutral).c

export function VerdictChip({label, level="neutral", sub, size=10, title}){
  const s = VLEVEL[level] || VLEVEL.neutral
  return (
    <span title={title} style={{fontSize:size,fontWeight:800,letterSpacing:"0.04em",color:s.c,background:s.b,
      border:`1px solid ${s.bd}`,borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap",display:"inline-block"}}>
      {label}{sub?<span style={{fontWeight:700,opacity:0.85}}> · {sub}</span>:null}
    </span>
  )
}

// ── team logos (ESPN CDN, graceful fallback to the abbreviation) ──────────────
const LOGO_CODE = { WAS: 'wsh', LA: 'lar' }   // nflverse → ESPN slug exceptions
export const logoUrl = abbr => `https://a.espncdn.com/i/teamlogos/nfl/500/${(LOGO_CODE[abbr] || abbr || '').toLowerCase()}.png`
export function TeamLogo({ abbr, size = 34 }) {
  const [err, setErr] = React.useState(false)
  const box = { width: size, height: size, flexShrink: 0, display: 'grid', placeItems: 'center' }
  if (abbr && !err) return (
    <div style={box}><img src={logoUrl(abbr)} alt={abbr} width={size} height={size}
      loading="lazy" onError={() => setErr(true)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /></div>
  )
  return <div style={{ ...box, fontFamily: T.mono, fontWeight: 800, fontSize: size * 0.34, color: T.muted }}>{abbr || '—'}</div>
}

// Position filter chips (All / RB / WR / TE / QB) — shared by the TD screens. `value` is the
// selected group ('ALL' or a position); rows are matched by their role/position prefix.
export const POS_GROUPS = ['ALL', 'RB', 'WR', 'TE', 'QB']
export const posOf = r => ((r && (r.role || r.pos || r.position)) || '').toUpperCase().slice(0, 2)
export function PosFilter({ value, onChange, counts }) {
  return (
    <div role="tablist" aria-label="Filter by position" style={{ display: 'flex', gap: 6, margin: '10px 0 4px' }}>
      {POS_GROUPS.map(g => {
        const on = value === g, n = counts ? counts[g] : null
        return (
          <button key={g} role="tab" aria-selected={on} onClick={() => onChange(g)} style={{ flex: 1, cursor: 'pointer',
            borderRadius: 9, padding: '7px 0', fontSize: 12.5, fontWeight: 800, border: `1px solid ${on ? T.green : T.border}`,
            background: on ? T.green : T.card, color: on ? '#04140D' : T.muted }}>
            {g === 'ALL' ? 'All' : g}{n != null && g !== 'ALL' ? <span style={{ fontWeight: 600, opacity: 0.75 }}> {n}</span> : null}
          </button>
        )
      })}
    </div>
  )
}

// A team as [logo] Nickname, inline — for sentences where the team is the subject
// ("[logo] Bills attack [logo] Raiders", "Our call: [logo] Jaguars").
export function TeamTag({ abbr, size = 16, color, weight = 800 }) {
  if (!abbr) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'middle', whiteSpace: 'nowrap', color, fontWeight: weight }}>
      <TeamLogo abbr={abbr} size={size} />{teamNick(abbr)}
    </span>
  )
}

// A game as two team logos — "[logo] @ [logo]" with the abbreviation small under each, so a casual
// fan who knows the helmet but not the city still reads it (and it still reads if a logo fails).
export function GameLogos({ away, home, size = 22, dim = false, color, names = false }) {
  const team = abbr => (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 1, opacity: dim ? 0.55 : 1 }}>
      <TeamLogo abbr={abbr} size={size} />
      <span style={{ fontSize: Math.max(10, Math.round(size * 0.4)), fontWeight: 800, color: color || T.muted, letterSpacing: names ? 0 : '0.02em', lineHeight: 1, whiteSpace: 'nowrap' }}>{names ? teamNick(abbr) : abbr}</span>
    </span>
  )
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {team(away)}
      <span style={{ fontSize: Math.max(10, Math.round(size * 0.42)), color: color || T.faint, fontWeight: 700, marginBottom: Math.round(size * 0.35) }}>@</span>
      {team(home)}
    </span>
  )
}

// Player face — headshot with the team logo as a small badge (pass `team`). If the headshot is
// missing or fails to load, the team logo stands in; with neither, initials. Circular, sized to
// the caller.
export function Avatar({url, src, name, size=38, team}){
  const [err,setErr] = React.useState(false)
  const [logoErr,setLogoErr] = React.useState(false)
  const img = url || src
  const initials = (name||'').split(' ').filter(Boolean).map(w=>w[0]).slice(0,2).join('').toUpperCase()
  const box = {width:size,height:size,borderRadius:"50%",flexShrink:0,overflow:"hidden",
    background:T.card2,border:`1px solid ${T.border2}`,display:"flex",alignItems:"center",justifyContent:"center"}
  const hasFace = img && !err
  const face = hasFace
    ? <div style={box}><img src={img} alt="" width={size} height={size} loading="lazy"
        onError={()=>setErr(true)} style={{width:"100%",height:"100%",objectFit:"cover"}}/></div>
    : team && !logoErr
      ? <div style={box}><img src={logoUrl(team)} alt={team} width={size} height={size} loading="lazy"
          onError={()=>setLogoErr(true)} style={{width:"78%",height:"78%",objectFit:"contain"}}/></div>
      : <div style={{...box,fontSize:size*0.34,fontWeight:800,color:T.muted}}>{initials||"—"}</div>
  if (!team || !hasFace) return face
  const b = Math.max(14, Math.round(size*0.46))
  return (
    <div style={{position:"relative",width:size,height:size,flexShrink:0}}>
      {face}
      {!logoErr && <span style={{position:"absolute",right:-3,bottom:-3,width:b,height:b,borderRadius:"50%",
        background:T.bg,border:`1px solid ${T.border2}`,display:"grid",placeItems:"center",overflow:"hidden"}}>
        <img src={logoUrl(team)} alt={team} width={b} height={b} loading="lazy" onError={()=>setLogoErr(true)}
          style={{width:"80%",height:"80%",objectFit:"contain"}}/></span>}
    </div>
  )
}

// ── primitives ──────────────────────────────────────────────────────────────
export function TierBadge({tier}){
  const t = TIER[tier]||TIER.WATCH
  return <span style={{fontSize:10,fontWeight:800,color:t.fg,background:t.bg,border:`1px solid ${t.bd}`,borderRadius:20,padding:"3px 9px",letterSpacing:"0.06em",whiteSpace:"nowrap"}}>{t.icon} {t.label}</span>
}

export function Stat({label,value,color=T.text,bg=T.card2}){
  return (
    <div style={{background:bg,border:`1px solid ${T.border2}`,borderRadius:8,padding:"8px 6px",textAlign:"center"}}>
      <div style={{fontSize:16,fontWeight:800,color,fontFamily:T.mono,lineHeight:1.1}}>{value}</div>
      <div style={{fontSize:10,color:T.muted,letterSpacing:"0.1em",marginTop:3,textTransform:"uppercase"}}>{label}</div>
    </div>
  )
}

export function GradeRing({grade,size=46}){
  const none = grade==null                     // e.g. no over/under posted yet: show a dash, not a fake number
  const col=none?T.muted:gradeColor(grade), r=(size-6)/2, c=2*Math.PI*r, off=c*(1-(none?0:grade)/100)
  return (
    <svg width={size} height={size} style={{transform:"rotate(-90deg)",flexShrink:0}}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border2} strokeWidth="3"/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth="3" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"/>
      <text x="50%" y="50%" dy="0.35em" textAnchor="middle" fill={col} style={{transform:"rotate(90deg)",transformOrigin:"center",fontSize:size*0.3,fontWeight:800,fontFamily:T.mono}}>{none?'—':grade}</text>
    </svg>
  )
}

export function SectionLabel({children, right, onRight}){
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",margin:"18px 0 10px"}}>
      <span style={{fontSize:12,fontWeight:800,color:T.muted,letterSpacing:"0.08em",textTransform:"uppercase"}}>{children}</span>
      {right && <button onClick={onRight} style={{background:"none",border:"none",color:T.green,fontSize:11,fontWeight:700,cursor:onRight?"pointer":"default"}}>{right}</button>}
    </div>
  )
}

export function Spinner(){
  return (
    <div style={{display:"flex",justifyContent:"center",padding:40}}>
      <div style={{width:30,height:30,borderRadius:"50%",border:`3px solid ${T.border}`,borderTopColor:T.green,animation:"spin 0.8s linear infinite"}}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

export function Empty({message="No data available"}){
  return <div style={{textAlign:"center",padding:"40px 16px",color:T.muted}}><div style={{fontSize:26,marginBottom:8}}>◆</div><div style={{fontSize:13,fontWeight:600}}>{message}</div></div>
}

// Note: LINEBRKR does not advise wager sizing. The board flags VALUE (a play that beats
// the posted price, +EV) but never a bet amount — so there's no bankroll / stake helper.

// ── search bar (shared across list tabs) ────────────────────────────────────
// One search control for every list surface — Rankings, Fantasy, Grids, Games,
// Futures, Record, Trends. Controlled input with a clear (×) affordance; the
// border greens while a query is active so it reads as "filtering".
export function SearchBar({value, onChange, placeholder="Search player, team, or opponent…", style}){
  return (
    <div style={{position:"relative", marginBottom:8, ...style}}>
      <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{width:"100%", boxSizing:"border-box", padding:"9px 30px 9px 12px", borderRadius:9,
          background:T.card, color:T.text, border:`1px solid ${value?T.green:T.border}`, fontSize:13, outline:"none"}} />
      {value && <button onClick={()=>onChange("")} aria-label="Clear search"
        style={{position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none",
          border:"none", color:T.muted, cursor:"pointer", fontSize:15, lineHeight:1}}>×</button>}
    </div>
  )
}

// Case-insensitive substring matcher over an arbitrary set of fields. Empty
// query ⇒ everything matches. Use for client-side name/team/opponent filtering:
//   rows.filter(searchMatch(q, r => [r.full_name, r.team, r.opp]))
export const searchMatch = (q, fields) => {
  const ql = (q||"").trim().toLowerCase()
  if(!ql) return () => true
  return item => (fields(item)||[]).filter(Boolean).join(" ").toLowerCase().includes(ql)
}

// ── bottom sheet + tabs (match UnifiedApp Sheet / Tabs) ─────────────────────
export function Sheet({children,onClose}){
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.88)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:460,margin:"0 auto",background:T.card2,borderRadius:"16px 16px 0 0",border:`1px solid ${T.border2}`,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"center",paddingTop:10}}><div style={{width:32,height:4,borderRadius:2,background:T.border2}}/></div>
        {children}
      </div>
    </div>
  )
}

export function Tabs({tabs,active,onSelect}){
  return (
    <div style={{display:"flex",gap:2,borderBottom:`1px solid ${T.border}`,marginBottom:14,overflowX:"auto",scrollbarWidth:"none"}}>
      {tabs.map(t=>{ const on=active===t; return (
        <button key={t} onClick={()=>onSelect(t)} style={{flex:"1 0 auto",padding:"9px 8px",border:"none",background:"transparent",color:on?T.green:T.muted,fontSize:12,fontWeight:on?800:600,cursor:"pointer",borderBottom:`2px solid ${on?T.green:"transparent"}`,marginBottom:-1}}>{t}</button>
      )})}
    </div>
  )
}

// ── bottom nav (matches UnifiedApp BottomNav) ───────────────────────────────
function NavIcon({type,color}){
  const c={fill:"none",stroke:color,strokeWidth:2,strokeLinecap:"round",strokeLinejoin:"round"}
  if(type==="home")  return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/></svg>
  if(type==="picks") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M12 2l2.4 6.5H21l-5.3 4 2 6.5L12 15l-5.7 4 2-6.5L3 8.5h6.6z"/></svg>
  if(type==="grids") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></svg>
  if(type==="games") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M12 3l9 9-9 9-9-9z"/><path d="M9 12l2 2 4-4"/></svg>
  if(type==="players") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><circle cx="9" cy="8" r="3"/><path d="M4 20a5 5 0 0110 0M16 6h5M16 10h5M16 14h3"/></svg>
  if(type==="more") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><circle cx="5" cy="12" r="1.4"/><circle cx="12" cy="12" r="1.4"/><circle cx="19" cy="12" r="1.4"/></svg>
  if(type==="futures") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 01-10 0z"/><path d="M5 4H3v1a3 3 0 003 3M19 4h2v1a3 3 0 01-3 3"/></svg>
  if(type==="fantasy") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M9 3v3h6V3M8 11h8M8 15h5"/></svg>
  if(type==="tdlab"||type==="touchdowns") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4.5"/><circle cx="12" cy="12" r="0.6" fill={color}/></svg>
  if(type==="playlab") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M9 3h6M10 3v6.2L4.6 18.3A1.8 1.8 0 006.1 21h11.8a1.8 1.8 0 001.5-2.7L14 9.2V3"/><path d="M7.2 15h9.6"/></svg>
  if(type==="studio") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
  // record + default: a track-record line chart
  return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/></svg>
}

// Six doors: the week's board, the generator, who scores, the games, every player, and the rest.
// Play Lab (was Card Studio, buried) sits second so "generate a play" is one tap from anywhere;
// Record moved to the top of More (also one tap from This Week's trust bar).
const NFL_NAV=[["home","This Week"],["playlab","Play Lab"],["touchdowns","Touchdowns"],["games","Games"],
               ["players","Players"],["more","More"]]

// `items` lets another sport (CFB) reuse the exact same nav chrome with its own doors:
// [id, label, icon?] — icon defaults to the id's NavIcon.
export function BottomNav({view,setView,items=NFL_NAV}){
  return (
    <nav style={{position:"fixed",bottom:0,left:0,right:0,zIndex:200,maxWidth:460,margin:"0 auto",background:"#0D1117",borderTop:`1px solid ${T.border}`,display:"flex",paddingBottom:"env(safe-area-inset-bottom,0)"}}>
      {items.map(([id,label,icon])=>{ const on=view===id; const col=on?T.green:T.muted; return (
        <button key={id} onClick={()=>setView(id)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"9px 4px 7px",border:"none",background:"none",cursor:"pointer",color:col,fontSize:10,fontWeight:700,letterSpacing:"0.04em",position:"relative"}}>
          {on&&<div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",width:26,height:2,background:T.green,borderRadius:"0 0 4px 4px"}}/>}
          <NavIcon type={icon||id} color={col}/>{label}
        </button>)})}
    </nav>
  )
}
